import type { OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MarketAdminDashboardComponent } from '../../../../features/market-admin-dashboard/market-admin-dashboard.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ManagerDashboardComponent } from '../../../../features/manager-dashboard/manager-dashboard.component';
import { AccessDeniedComponent } from '../../../../shared/components/access-denied/access-denied.component';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { UserManagementService } from '../../../services/user-management.service';
import type { IUser, SystemRole } from '../../../../shared/models/user.model';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MarketAdminDashboardComponent, MatProgressSpinnerModule, ManagerDashboardComponent, AccessDeniedComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private authService = inject(AuthService);
  private userService = inject(UserManagementService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  showLoading = true;
  currentUser: IUser | null = null;
  userRole = '';
  selectedRoleContext: SystemRole | null = null; // Store the FULL role context with market details

  ngOnInit(): void {
    // Check if user data is already available from app.config initialization
    const existingUser = this.authService.userDataValue;
    if (existingUser) {
      this.currentUser = existingUser;
      const firstRole = existingUser.system_role_info && existingUser.system_role_info.length > 0 ? existingUser.system_role_info[0].role_name : '';
      this.userRole = firstRole;
      this.showLoading = false;

      const validRoles = ['system admin', 'market admin', 'business unit admin', 'bu admin', 'manager','compliance bp'];
      if (!validRoles.includes(this.userRole.toLowerCase())) {
        this.router.navigate(['/access-denied']);
        return;
      }
      this.cdr.markForCheck();
    }


    // Listen for selected role CONTEXT changes (includes market_id, privileges, etc.)
    this.authService.selectedRoleContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roleContext) => {
          if (roleContext) {
            // Store the FULL role context (includes market_id, market_name, privileges, etc.)
            this.selectedRoleContext = roleContext;
            this.userRole = roleContext.role_name;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  onLoadingEvent(event: boolean): void {
    this.cdr.markForCheck();
    this.showLoading = event;
  }

  get isAdminRole(): boolean {
    const role = this.userRole?.toLowerCase();
    return role === 'system admin' ||
           role === 'market admin' ||
           role === 'business unit admin' ||
           role === 'compliance bp' ||
           role === 'bu admin';
  }

  get isManagerRole(): boolean {
    return this.userRole?.toLowerCase() === 'manager';
  }
}
